import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';

import CancelButton from '../../components/psc/lib/CancelButton'
import SaveButton from '../../components/psc/lib/SaveButton'
import ErrorMessage from '../../components/psc/lib/ErrorMessage'

const SharepointApplication = () => {
    const objectUrl = '/v0/sharepoint_application'

    // Fields
    const [tenantID, setTenantID] = useState('')
    const [clientID, setClientID] = useState('')
    const [clientSecret, setClientSecret] = useState('')

    // Submit
    const [submitActive, setSubmitActive] = useState(false)
    const [error, setError] = useState(null)
    const [validationResult, setValidationResult] = useState('')

    useEffect(() => {
        fetchObject()
    }, [])

    const fetchObject = () => {
        fetch(objectUrl, { credentials: 'same-origin' })
            .then(function(response) {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error(response.statusText)
                }
            })
            .then(function(json) {
                if(json.error) {
                    throw new Error(json.error)
                }

                fromBackend(json.result)
            })
            .catch(function(error) {
                console.log(error)
            })
    }

    const fromBackend = (obj) => {
        setTenantID(obj.tenant_id)
        setClientID(obj.client_id)
        setClientSecret(obj.client_secret)
    }

    const toBackend = () => {
        return {
            tenant_id: tenantID.trim(),
            client_id: clientID.trim(),
            client_secret: clientSecret.trim(),
        }
    }

    const validateBeforeSubmit = () => {
        let errors = []

        if ( ! tenantID ) {
            errors.push('Tenant ID is required')
        }

        if ( ! clientID ) {
            errors.push('Client ID is required')
        }

        if ( ! clientSecret ) {
            errors.push('Client secret is required')
        }

        if ( errors.length > 0 ) {
            setError(errors)
            return false
        }

        return true
    }

    const handleSubmitError = (props) => {
        setSubmitActive(false)
        setError([ props.message ])
    }
    
    const handleSubmit = (event) => {
        if( event ) {
            event.preventDefault()
        }
        if( submitActive || !validateBeforeSubmit() ) {
            return null
        }

        setSubmitActive(true)

        return {
            objType: 'sharepoint_application',
            data: toBackend(),
        }
    }

    const handleValidate = (event) => {
        if( event ) {
            event.preventDefault()
        }
        if( submitActive || !validateBeforeSubmit() ) {
            return null
        }

        setSubmitActive(true)

        return {
            objType: 'sharepoint_application/validate',
            data: toBackend(),
            navigateOverride: () => {},
            successCallback: (ret) => {
                setSubmitActive(false)
                if( ret.result.is_valid ) {
                    setValidationResult("✅ Sharepoint configuration appears valid")
                } else {
                    setValidationResult("❌ " + ret.result.error)
                }
            }
        }
    }
    
    let requiredIndicator = (<label className="form-required-star">&nbsp;*</label>);

    return (
        <div className="wrapper wrapper-content">
            <div className="row">
                <div className="col-lg-12">
                    <div className="ibox float-e-margins">
                        <div className="ibox-title">
                            <h2>Sharepoint Application</h2>
                        </div>
                        <div className="ibox-content">
                            <form className="form-horizontal" autoComplete="off" onSubmit={(e) => handleSubmit(e)}>
                                <div className="form-group">
                                    <label className="col-sm-2"></label>
                                    <div className="col-sm-8">
                                        <p>This configuration is for allowing GNC Go to integrate with Sharepoint.</p>
                                        <p>
                                            Enter you Azure Application details below. See our
                                            <Link
                                                to="https://support.gncgo.space/kb/guide/en/supported-connections-EGkcDKQM9f/Steps/4608763"
                                                target="_blank"
                                            > Knowledge base </Link>
                                            for help on setting up your Azure Application.
                                        </p>
                                    </div>
                                </div>
                                
                                <div className="form-group">
                                    <label className="col-sm-2 control-label">Tenant ID
                                        {requiredIndicator}
                                    </label>
                                    <div className="col-sm-8">
                                        <input className="form-control" type="text"
                                               autoComplete="off"
                                               value={tenantID}
                                               onChange={(e) => setTenantID(e.target.value)}
                                               required
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">Client ID
                                        {requiredIndicator}
                                    </label>
                                    <div className="col-sm-8">
                                        <input className="form-control" type="text"
                                               autoComplete="off"
                                               data-form-type="other"
                                               value={clientID}
                                               onChange={(e) => setClientID(e.target.value)}
                                               required 
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">Client Secret
                                        {requiredIndicator}
                                    </label>
                                    <div className="col-sm-8">
                                        <input className="form-control" type="password"
                                               autoComplete="new-password"
                                               data-form-type="other"
                                               value={clientSecret}
                                               onChange={(e) => setClientSecret(e.target.value)}
                                               required 
                                        />
                                    </div>
                                </div>

                                <div className="hr-line-dashed"></div>

                                <div className="form-group">
                                    <div className="col-sm-10 col-sm-offset-2">
                                        <SaveButton
                                            buttonVariant='warning'
                                            visible={true}
                                            spinnerActive={submitActive}
                                            label='Validate Configuration'
                                            onClick={handleValidate}
                                            onError={handleSubmitError}
                                        />
                                        {validationResult}
                                    </div>
                                </div>
                                
                                <div className="hr-line-dashed"></div>

                                <div className="form-group">
                                    <ErrorMessage
                                        error={error}
                                    />

                                    <div className="col-sm-10 col-sm-offset-2">
                                        <CancelButton />
                                        
                                        <SaveButton
                                            buttonVariant='primary'
                                            visible={true}
                                            spinnerActive={submitActive}
                                            label='Save Changes'
                                            onClick={handleSubmit}
                                            onError={handleSubmitError}
                                        />

                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
};

export default SharepointApplication
